import { ISort, ITableState } from '../../../base';
import { loadTableStateFromSession } from '../../../common/utils/sessionStorage';
import { emptyTableState, tableDataBaseUrls, TableIds } from '../../../common';
import { ITablesSlice } from './tablesSlice';

const getQueryParams = (): {
    queryParams: Record<string, string>;
    tableId: string | undefined;
} => {
    const url = new URL(window.location.href);
    const pathname = url.pathname.replace(/^\/|\/$/g, '');
    const baseUrl = `${pathname.split('/').pop()}/` || '';
    const tableId = Object.keys(tableDataBaseUrls).find(
        (id) => tableDataBaseUrls[id] === baseUrl
    );

    const params = new URLSearchParams(url.search);
    const queryParams: Record<string, string> = {};

    params.forEach((value, key) => {
        queryParams[key] = value;
    });

    return { queryParams, tableId };
};

export const createFilterObjectFromQueryParams = (
    queryParams: Record<string, string>
) => {
    return Object.entries(queryParams)
        .filter(([key]) => !['search', 'ordering', 'page'].includes(key))
        .reduce(
            (acc, [filterId, values]) => {
                acc[filterId] = values.includes(',')
                    ? values.split(',')
                    : [values];
                return acc;
            },
            {} as Record<string, Array<unknown>>
        );
};

export const parseQueryParamsToTableState = (
    queryParams: Record<string, string>,
    tableId: string
): ITableState => {
    const tableState: ITableState = {
        id: tableId,
        filters: {},
        sort: {},
        defaultSortKey: 'id',
        search: queryParams.search || '',
        isLoading: false,
        isLoadingMore: false,
        currentPage: parseInt(queryParams.page || '1', 10),
        totalPages: 1,
        totalEntries: 0,
        currentEntitiesCount: 0,
        selectedRows: {},
        initialLoad: true,
    };

    if (queryParams.ordering) {
        const isDesc = queryParams.ordering.startsWith('-');
        const sortKey = isDesc
            ? queryParams.ordering.slice(1)
            : queryParams.ordering;
        const sortObject: ISort = {
            sortDirection: isDesc ? 'desc' : 'asc',
            sortKey,
        };
        tableState.sort = sortObject;
    }

    tableState.filters = createFilterObjectFromQueryParams(queryParams);

    return tableState;
};

export const createInitialState = (): ITablesSlice => {
    const { queryParams, tableId } = getQueryParams();
    const persistedState = loadTableStateFromSession();

    // First, create the initial state for all tableIds
    const initialState = Object.values(TableIds).reduce((acc, id) => {
        acc[id] = {
            state: persistedState?.[id] || emptyTableState(id),
            data: [],
        };
        return acc;
    }, {} as ITablesSlice);

    // If tableId and queryParams are provided, override the state for that specific tableId
    if (tableId && queryParams) {
        const tableState = parseQueryParamsToTableState(queryParams, tableId);
        initialState[tableId] = {
            state: tableState, // Override the state for the specific tableId
            data: [],
        };
    }

    return initialState;
};
